import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
        setMobileCatOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isHome = location.pathname === '/';
  const isArticle = location.pathname.includes('guia') || location.pathname.includes('lista') || location.pathname.includes('comparacao') || location.pathname.includes('seguranca') || location.pathname.includes('economizar') || location.pathname.includes('produtos') || location.pathname.includes('fornecedores') || location.pathname.includes('iniciantes') || location.pathname.includes('erros') || location.pathname.includes('categorias');

  const mainLinks = [
    { label: 'Início', path: '/' },
    { label: 'Guia de Compras', path: '/guia-hoobuy-spreadsheet-2026' },
    { label: 'Categorias', path: '/#categorias' },
    { label: 'Blog SEO', path: '/#blog' },
  ];

  const seoLinks = [
    { label: 'Listas Atualizadas', path: '/listas-hoobuy-atualizadas-2026' },
    { label: 'Hoobuy vs Pandabuy', path: '/comparacao-hoobuy-pandabuy' },
    { label: 'Segurança', path: '/seguranca-compras-hoobuy' },
    { label: 'Economizar', path: '/como-economizar-hoobuy' },
  ];

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileCatOpen(false);
  };

  const navbarBg = scrolled
    ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
    : isHome
      ? 'bg-background-950/25 backdrop-blur-sm'
      : isArticle
        ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
        : 'bg-background-50';

  const textColor = scrolled
    ? 'text-foreground-950'
    : isHome
      ? 'text-background-50'
      : 'text-foreground-950';

  const linkColor = scrolled
    ? 'text-foreground-700'
    : isHome
      ? 'text-background-50/90'
      : 'text-foreground-700';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navbarBg}`}>
      <div className="px-4 md:px-6 py-3.5">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group" onClick={closeMobile}>
            <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-105 ${scrolled || !isHome ? 'bg-primary-500' : 'bg-background-50/20'}`}>
              <span className={`font-bold text-sm font-label ${scrolled || !isHome ? 'text-background-50' : 'text-background-50'}`}>H</span>
            </div>
            <span className={`font-heading text-xl font-bold transition-colors duration-300 ${textColor}`}>
              HooBuy
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            {mainLinks.map((link) =>
              link.path.startsWith('/#') ? (
                <a
                  key={link.label}
                  href={link.path}
                  className={`text-sm font-semibold font-label transition-colors duration-200 hover:text-primary-400 whitespace-nowrap ${linkColor}`}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.path}
                  className={`text-sm font-semibold font-label transition-colors duration-200 hover:text-primary-400 whitespace-nowrap ${
                    location.pathname === link.path ? 'text-primary-500 font-bold' : linkColor
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}

            <div className="relative group">
              <button className={`text-sm font-semibold font-label transition-colors duration-200 hover:text-primary-400 whitespace-nowrap flex items-center gap-1 ${linkColor}`}>
                + Artigos
                <i className="ri-arrow-down-s-line text-xs"></i>
              </button>
              <div className="absolute top-full right-0 mt-2 bg-background-50 border border-background-200/70 rounded-xl shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 min-w-[200px] py-2">
                {seoLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.path}
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors duration-150 whitespace-nowrap"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm font-semibold font-label px-5 py-2.5 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                scrolled || !isHome
                  ? 'bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600'
                  : 'bg-background-50 text-foreground-950 hover:bg-background-100'
              }`}
            >
              {t('cta_access')}
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center ${textColor}`}
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div ref={mobileRef} className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 flex flex-col gap-1">
            {mainLinks.map((link) =>
              link.path.startsWith('/#') ? (
                <a
                  key={link.label}
                  href={link.path}
                  onClick={closeMobile}
                  className="text-foreground-700 hover:text-primary-500 text-sm font-semibold font-label py-3 transition-colors duration-200"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.path}
                  onClick={closeMobile}
                  className={`text-sm font-semibold font-label py-3 transition-colors duration-200 ${
                    location.pathname === link.path ? 'text-primary-500' : 'text-foreground-700 hover:text-primary-500'
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}

            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between text-foreground-700 hover:text-primary-500 text-sm font-semibold font-label py-3 transition-colors duration-200 w-full"
            >
              <span>+ Artigos SEO</span>
              <i className={`text-sm transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''} ri-arrow-down-s-line`}></i>
            </button>

            {mobileCatOpen && (
              <div className="pl-3 border-l-2 border-background-200/70 ml-2 flex flex-col gap-1">
                {seoLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.path}
                    onClick={closeMobile}
                    className="text-foreground-600 hover:text-primary-500 text-sm py-2.5 transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}

            <div className="pt-2 border-t border-background-200/70 mt-1">
              <p className="text-xs text-foreground-400 font-label mb-2 px-1">Categorias Rápidas</p>
              <div className="grid grid-cols-2 gap-1">
                {['Shoes','Hoodies','T-Shirts','Jackets','Pants','Headwear','Sets','Underwear','Jersey','Accessories','Bags','Belts','Perfumes','Other'].map((cat, i) => (
                  <a
                    key={cat}
                    href={`https://kaspreadsheet.com/search-${911 + i}/name/Shoes.html`}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeMobile}
                    className="text-xs text-foreground-500 hover:text-primary-500 py-2 px-1 transition-colors duration-200"
                  >
                    {cat}
                  </a>
                ))}
              </div>
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeMobile}
              className="text-sm font-semibold font-label bg-primary-500 text-background-50 dark:text-foreground-950 px-5 py-3 rounded-full hover:bg-primary-600 transition-all duration-200 text-center whitespace-nowrap cursor-pointer mt-2"
            >
              {t('cta_access')}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}